import React from "react";
import { slotText } from "@/hooks/useUiSurface";

/**
 * <Slot> - renders a single UI slot from a surface, with a safe fallback.
 *
 * Usage:
 *   <Slot map={slotMap} k="headline" fallback="Plan Your Experience" />
 *   <Slot map={slotMap} k="primary_cta_label" as="span" fallback="Save" />
 *
 * Slot types:
 *   - text / button / link  -> text content
 *   - rich_text             -> rendered as innerHTML (only set in Airtable by admins)
 *   - image                 -> default_value is treated as a URL, props.alt for alt text
 *   - icon                  -> renders nothing here; consumers read slot.props.icon themselves
 *
 * The component never throws if a slot is missing; it always shows the fallback.
 */
export const Slot = ({
  map,
  k,
  fallback = "",
  as: Tag = "span",
  className = "",
  ...rest
}) => {
  const slot = map?.[k];
  const value = slot?.body ?? slot?.default_value ?? fallback;
  const type = slot?.slot_type || "text";

  if (type === "image") {
    if (!value) return null;
    const alt = slot?.props?.alt || "";
    return (
      <img
        src={value}
        alt={alt}
        className={className}
        data-testid={`slot-${k}`}
        {...rest}
      />
    );
  }

  if (type === "rich_text") {
    return (
      <Tag
        className={className}
        data-testid={`slot-${k}`}
        // admin-edited only; not user input
        dangerouslySetInnerHTML={{ __html: value || "" }}
        {...rest}
      />
    );
  }

  return (
    <Tag className={className} data-testid={`slot-${k}`} {...rest}>
      {value}
    </Tag>
  );
};

// Convenience helper exported for places that can't use JSX components
// (e.g. setting a button's title or aria-label).
export { slotText };
